import { useState } from 'react';
import { mensajeDeError } from '@/lib/errores';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, PawPrint, Phone } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Turnstile, turnstileActivo } from '@/components/auth/Turnstile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * El dueño de la mascota entra con su teléfono, escrito como quiera: se
 * normaliza a dígitos y se traduce al correo sintético que usa Supabase. La
 * contraseña por defecto son los mismos dígitos, y desde el 27/09 puede
 * cambiarla en su perfil.
 *
 * **Lleva captcha, como el login del staff.** Esta pantalla se quedó sin él
 * cuando se montó Turnstile el 25/09, y el resultado fue que **el portal
 * quedó inaccesible para todas las clientas**: GoTrue exige el token en el
 * endpoint de login, que es el MISMO para los dos, así que cada intento
 * moría con `captcha_failed` sin llegar a mirar la contraseña. Nadie lo
 * reportó porque el portal aún se usaba poco; el último acceso registrado es
 * del 24/09, la víspera.
 *
 * La lección: al montar un captcha hay que contar TODAS las pantallas que
 * llaman a `signInWithPassword`, no solo la principal.
 */
export function PortalLogin() {
  const nav = useNavigate();
  const [phone, setPhone] = useState('+57 320 111 2233');
  const [password, setPassword] = useState('573201112233');
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  // Dos banderas, no una: `!captcha` significa a la vez «todavía resolviendo»
  // y «no va a resolver jamás». Con una sola, un captcha que se cuelga deja
  // el botón muerto para siempre y nadie entra.
  const [captchaTermino, setCaptchaTermino] = useState(false);
  // El token se gasta al usarse: tras un intento fallido hay que reiniciarlo.
  const [reiniciarCaptcha, setReiniciarCaptcha] = useState(0);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = phone.replace(/[^0-9]/g, '');
    if (digits.length < 7) { toast.error('Ingresa un número de teléfono válido'); return; }
    if (!password.trim()) { toast.error('Ingresa tu contraseña'); return; }
    setLoading(true);
    const email = `${digits}@portal.groomingpro.local`;
    // El token viaja con las credenciales y lo valida GoTrue contra
    // Cloudflare desde el servidor. Se manda solo si existe: un
    // `captchaToken: null` GoTrue lo trata como vacío y lo rechaza.
    const { error } = await supabase.auth.signInWithPassword(
      captcha ? { email, password, options: { captchaToken: captcha } } : { email, password },
    );
    setLoading(false);
    if (error) {
      setReiniciarCaptcha((n) => n + 1);
      setCaptcha(null);
      setCaptchaTermino(false);

      // Un fallo del captcha no es una contraseña mal escrita. Decir lo mismo
      // en los dos casos manda a la clienta a cambiar algo que está bien.
      if (/captcha|turnstile/i.test(error.message)) {
        toast.error('No se pudo verificar que eres una persona. Recarga la página e intenta de nuevo.');
        return;
      }
      const m = error.message?.toLowerCase() ?? '';
      if (m.includes('invalid') || m.includes('credentials')) {
        toast.error('Número o contraseña incorrectos. Recuerda: tu contraseña por defecto es tu mismo número.');
      } else {
        toast.error(mensajeDeError(error));
      }
      return;
    }
    toast.success('¡Bienvenido!');
    nav('/portal');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-400 text-white shadow-soft">
            <PawPrint className="h-8 w-8" />
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold">Portal de Clientes</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sigue a tu mascota en tiempo real
          </p>
        </div>

        <form onSubmit={onSubmit} className="rounded-3xl border border-border bg-card p-8 shadow-soft">
          <h2 className="mb-6 text-xl font-semibold">Ingresa con tu teléfono</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-primary" /> Teléfono
              </Label>
              <Input
                id="phone" type="tel" inputMode="tel" autoComplete="tel"
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+57 300 123 4567"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} required
              />
              <p className="text-[11px] text-muted-foreground">
                Por defecto, tu contraseña es <strong>tu mismo número de teléfono</strong> (sin espacios ni signos).
              </p>
            </div>
            <Turnstile
              onToken={(t) => { setCaptcha(t); setCaptchaTermino(true); }}
              reiniciar={reiniciarCaptcha}
            />

            {/* Ante la duda se deja pulsar y que GoTrue decida: su rechazo al
                menos da un mensaje; un botón muerto no da ninguno. */}
            <Button
              type="submit" className="w-full" size="lg"
              disabled={loading || (turnstileActivo && !captcha && !captchaTermino)}
            >
              {loading ? 'Ingresando…' : 'Ingresar'}
            </Button>
          </div>
        </form>

        <div className="space-y-2 text-center text-xs text-muted-foreground">
          <p className="flex items-center justify-center gap-1">
            <Heart className="h-3 w-3 fill-rose-500 text-rose-500" />
            Para dueños de mascotas
          </p>
          <p>
            ¿Eres staff?{' '}
            {/* inline-flex + min-h-[44px]: mínimo táctil. Ver button.tsx. */}
            <Link to="/login" className="inline-flex min-h-[44px] items-center font-medium text-primary hover:underline">
              Ingresa aquí
            </Link>
          </p>

          {/* Aquí es donde el dueño entrega su número por primera vez. Si la
              política no está a la vista en este punto, no está a la vista
              cuando importa. */}
          <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t border-border pt-3 text-[11px]">
            <Link to="/politica-tratamiento-datos" className="hover:text-foreground hover:underline">
              Tratamiento de datos
            </Link>
            <Link to="/terminos-y-condiciones" className="hover:text-foreground hover:underline">
              Términos y condiciones
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
